/**
 * Contrast guard for writer-supplied segment colors (MP-182).
 *
 * Transcript writers (notably the desktop CoUnSiL-heritage apps, which
 * display white-on-black captions) may export their literal display colors
 * in segment formatting. Viewers applying those verbatim can produce
 * invisible text — e.g. white segments on the viewer's white background.
 *
 * The guard's contract is **prove legibility or drop**. For an accessibility
 * product we must never render text we cannot prove is visible:
 *
 *   - effective fg = segment color  (else the viewer's text color)
 *   - effective bg = segment background (else the viewer's background)
 *   - both parse AND pair contrast >= MIN_CONTRAST → keep the writer's colors
 *   - illegible pair, OR any supplied value we cannot parse → drop BOTH colors
 *     so the segment inherits the viewer's own (always-legible) colors;
 *     bold/italic/etc. are untouched by this guard.
 *
 * The reference surface is the *viewer's* effective colors (its background and
 * text settings) — the surface the transcript is actually painted on — not the
 * app's global light/dark chrome. Measuring against the wrong surface was the
 * core MP-182 defect.
 */
/**
 * WCAG 1.4.3 "large text" minimum. Transcript text renders at the viewer's
 * enlarged reading size, and the guard's job is catching *invisible* text
 * (ratios near 1:1), not enforcing full AA on deliberate writer styling.
 */
const MIN_CONTRAST = 3;
/**
 * The CSS extended (X11) named-color keywords, as hex. Resolved through the
 * hex parser below so there is a single source of truth for the RGB math.
 * The full table matters here: a writer picking a light keyword like
 * `whitesmoke`/`ivory`/`azure` is exactly the invisible-on-white case, and an
 * unrecognised keyword would otherwise be dropped (safe but loses legible
 * styling). `transparent` is intentionally absent → parses as null → dropped.
 */
const NAMED_COLORS = {
    aliceblue: '#f0f8ff',
    antiquewhite: '#faebd7',
    aqua: '#00ffff',
    aquamarine: '#7fffd4',
    azure: '#f0ffff',
    beige: '#f5f5dc',
    bisque: '#ffe4c4',
    black: '#000000',
    blanchedalmond: '#ffebcd',
    blue: '#0000ff',
    blueviolet: '#8a2be2',
    brown: '#a52a2a',
    burlywood: '#deb887',
    cadetblue: '#5f9ea0',
    chartreuse: '#7fff00',
    chocolate: '#d2691e',
    coral: '#ff7f50',
    cornflowerblue: '#6495ed',
    cornsilk: '#fff8dc',
    crimson: '#dc143c',
    cyan: '#00ffff',
    darkblue: '#00008b',
    darkcyan: '#008b8b',
    darkgoldenrod: '#b8860b',
    darkgray: '#a9a9a9',
    darkgreen: '#006400',
    darkgrey: '#a9a9a9',
    darkkhaki: '#bdb76b',
    darkmagenta: '#8b008b',
    darkolivegreen: '#556b2f',
    darkorange: '#ff8c00',
    darkorchid: '#9932cc',
    darkred: '#8b0000',
    darksalmon: '#e9967a',
    darkseagreen: '#8fbc8f',
    darkslateblue: '#483d8b',
    darkslategray: '#2f4f4f',
    darkslategrey: '#2f4f4f',
    darkturquoise: '#00ced1',
    darkviolet: '#9400d3',
    deeppink: '#ff1493',
    deepskyblue: '#00bfff',
    dimgray: '#696969',
    dimgrey: '#696969',
    dodgerblue: '#1e90ff',
    firebrick: '#b22222',
    floralwhite: '#fffaf0',
    forestgreen: '#228b22',
    fuchsia: '#ff00ff',
    gainsboro: '#dcdcdc',
    ghostwhite: '#f8f8ff',
    gold: '#ffd700',
    goldenrod: '#daa520',
    gray: '#808080',
    green: '#008000',
    greenyellow: '#adff2f',
    grey: '#808080',
    honeydew: '#f0fff0',
    hotpink: '#ff69b4',
    indianred: '#cd5c5c',
    indigo: '#4b0082',
    ivory: '#fffff0',
    khaki: '#f0e68c',
    lavender: '#e6e6fa',
    lavenderblush: '#fff0f5',
    lawngreen: '#7cfc00',
    lemonchiffon: '#fffacd',
    lightblue: '#add8e6',
    lightcoral: '#f08080',
    lightcyan: '#e0ffff',
    lightgoldenrodyellow: '#fafad2',
    lightgray: '#d3d3d3',
    lightgreen: '#90ee90',
    lightgrey: '#d3d3d3',
    lightpink: '#ffb6c1',
    lightsalmon: '#ffa07a',
    lightseagreen: '#20b2aa',
    lightskyblue: '#87cefa',
    lightslategray: '#778899',
    lightslategrey: '#778899',
    lightsteelblue: '#b0c4de',
    lightyellow: '#ffffe0',
    lime: '#00ff00',
    limegreen: '#32cd32',
    linen: '#faf0e6',
    magenta: '#ff00ff',
    maroon: '#800000',
    mediumaquamarine: '#66cdaa',
    mediumblue: '#0000cd',
    mediumorchid: '#ba55d3',
    mediumpurple: '#9370db',
    mediumseagreen: '#3cb371',
    mediumslateblue: '#7b68ee',
    mediumspringgreen: '#00fa9a',
    mediumturquoise: '#48d1cc',
    mediumvioletred: '#c71585',
    midnightblue: '#191970',
    mintcream: '#f5fffa',
    mistyrose: '#ffe4e1',
    moccasin: '#ffe4b5',
    navajowhite: '#ffdead',
    navy: '#000080',
    oldlace: '#fdf5e6',
    olive: '#808000',
    olivedrab: '#6b8e23',
    orange: '#ffa500',
    orangered: '#ff4500',
    orchid: '#da70d6',
    palegoldenrod: '#eee8aa',
    palegreen: '#98fb98',
    paleturquoise: '#afeeee',
    palevioletred: '#db7093',
    papayawhip: '#ffefd5',
    peachpuff: '#ffdab9',
    peru: '#cd853f',
    pink: '#ffc0cb',
    plum: '#dda0dd',
    powderblue: '#b0e0e6',
    purple: '#800080',
    rebeccapurple: '#663399',
    red: '#ff0000',
    rosybrown: '#bc8f8f',
    royalblue: '#4169e1',
    saddlebrown: '#8b4513',
    salmon: '#fa8072',
    sandybrown: '#f4a460',
    seagreen: '#2e8b57',
    seashell: '#fff5ee',
    sienna: '#a0522d',
    silver: '#c0c0c0',
    skyblue: '#87ceeb',
    slateblue: '#6a5acd',
    slategray: '#708090',
    slategrey: '#708090',
    snow: '#fffafa',
    springgreen: '#00ff7f',
    steelblue: '#4682b4',
    tan: '#d2b48c',
    teal: '#008080',
    thistle: '#d8bfd8',
    tomato: '#ff6347',
    turquoise: '#40e0d0',
    violet: '#ee82ee',
    wheat: '#f5deb3',
    white: '#ffffff',
    whitesmoke: '#f5f5f5',
    yellow: '#ffff00',
    yellowgreen: '#9acd32',
};
/** HSL → RGB (h in degrees, s/l in 0..100). */
function hslToRgb(h, s, l) {
    const sN = s / 100;
    const lN = l / 100;
    const c = (1 - Math.abs(2 * lN - 1)) * sN;
    const hp = (((h % 360) + 360) % 360) / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0;
    let g = 0;
    let b = 0;
    if (hp < 1)
        [r, g, b] = [c, x, 0];
    else if (hp < 2)
        [r, g, b] = [x, c, 0];
    else if (hp < 3)
        [r, g, b] = [0, c, x];
    else if (hp < 4)
        [r, g, b] = [0, x, c];
    else if (hp < 5)
        [r, g, b] = [x, 0, c];
    else
        [r, g, b] = [c, 0, x];
    const m = lN - c / 2;
    return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}
/**
 * Parse a CSS color into RGB, or null when we cannot interpret it.
 * Supports: named keywords, #rgb/#rgba/#rrggbb/#rrggbbaa hex, comma- or
 * space-separated rgb()/rgba() (incl. `/ alpha`), and hsl()/hsla(). Alpha is
 * ignored — the guard reasons about hue/luminance, not opacity.
 */
export function parseCssColor(value) {
    if (!value)
        return null;
    const v = value.trim().toLowerCase();
    const named = NAMED_COLORS[v];
    if (named)
        return parseCssColor(named);
    const hex = v.match(/^#([0-9a-f]+)$/);
    if (hex) {
        const h = hex[1];
        if (h.length === 3 || h.length === 4) {
            return [
                Number.parseInt(h[0] + h[0], 16),
                Number.parseInt(h[1] + h[1], 16),
                Number.parseInt(h[2] + h[2], 16),
            ];
        }
        if (h.length === 6 || h.length === 8) {
            return [
                Number.parseInt(h.slice(0, 2), 16),
                Number.parseInt(h.slice(2, 4), 16),
                Number.parseInt(h.slice(4, 6), 16),
            ];
        }
        return null;
    }
    const rgb = v.match(/^rgba?\(\s*(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})\s*(?:[,/]\s*[0-9.]+%?\s*)?\)$/);
    if (rgb) {
        const channels = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
        if (channels.every((c) => c <= 255))
            return channels;
        return null;
    }
    const hsl = v.match(/^hsla?\(\s*(\d{1,3}(?:\.\d+)?)(?:deg)?(?:\s*,\s*|\s+)(\d{1,3}(?:\.\d+)?)%(?:\s*,\s*|\s+)(\d{1,3}(?:\.\d+)?)%\s*(?:[,/]\s*[0-9.]+%?\s*)?\)$/);
    if (hsl) {
        return hslToRgb(Number(hsl[1]), Number(hsl[2]), Number(hsl[3]));
    }
    return null;
}
/** WCAG relative luminance. */
function relativeLuminance([r, g, b]) {
    const lin = [r, g, b].map((c) => {
        const s = c / 255;
        return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}
/** WCAG contrast ratio between two colors (1..21). */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
/**
 * Decide which writer-supplied segment colors are safe to render on the
 * viewer's surface. See module doc-comment for the rules. `theme` carries the
 * viewer's *own* background/text colors — the surface the text renders against.
 */
export function legibleSegmentColors(color, backgroundColor, theme) {
    if (!color && !backgroundColor) {
        return { color: undefined, backgroundColor: undefined };
    }
    const fg = parseCssColor(color);
    const bg = parseCssColor(backgroundColor);
    // Prove-legibility-or-drop: any supplied value we cannot parse means we
    // cannot guarantee visibility, so fall back to the viewer's own colors
    // rather than risk invisible text (the recurring MP-182 failure).
    if ((color && !fg) || (backgroundColor && !bg)) {
        return { color: undefined, backgroundColor: undefined };
    }
    const themeBg = parseCssColor(theme.themeBg);
    const themeText = parseCssColor(theme.themeText);
    if (!themeBg || !themeText) {
        return { color: undefined, backgroundColor: undefined };
    }
    const effectiveFg = fg ?? themeText;
    const effectiveBg = bg ?? themeBg;
    if (contrastRatio(effectiveFg, effectiveBg) >= MIN_CONTRAST) {
        return { color, backgroundColor };
    }
    return { color: undefined, backgroundColor: undefined };
}
//# sourceMappingURL=segment-contrast.js.map